import { formatCurrency } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import FeeScheduleBillingCode from "work/values/fee-schedule-billing-code/fee-schedule-billing-code";
import FeeScheduleCategoryDescription
  from "work/values/fee-schedule-category-description/fee-schedule-category-description";
import FeeScheduleCategoryName from "work/values/fee-schedule-category-name/fee-schedule-category-name";
import Fee, { FeeJSON } from "work/values/fee/fee";

export interface FeeScheduleCategoryJSON {
  id: string;
  name: string;
  description: string | null;
  fee: FeeJSON | null;
  billingCode: string | null;
  currencyIsMasked?: boolean;
}

export default class FeeScheduleCategory {
  id: Guid;
  name: FeeScheduleCategoryName;
  description: FeeScheduleCategoryDescription | null;
  fee: Fee | null;
  billingCode: FeeScheduleBillingCode | null;

  constructor(
    name: FeeScheduleCategoryName,
    description: FeeScheduleCategoryDescription | null,
    fee: Fee | null,
    billingCode: FeeScheduleBillingCode | null,
    id: Guid
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.fee = fee;
    this.billingCode = billingCode;
  }

  public isEqualTo(other: object | null | undefined): boolean {
    if (!other) return false;
    if (!(other instanceof FeeScheduleCategory)) return false;
    return (Boolean(
      this.name?.isEqualTo(other.name) &&
      this.description?.isEqualTo(other.description) &&
      ((!this.fee && !other.fee) || this.fee?.isEqualTo(other.fee)) &&
      (this.billingCode ? this.billingCode.isEqualTo(other.billingCode) : !other.billingCode)
    ));
  }

  public clone(): FeeScheduleCategory {
    return new FeeScheduleCategory(
      this.name,
      this.description,
      this.fee,
      this.billingCode,
      this.id
    );
  }

  public static fromJSON(json: FeeScheduleCategoryJSON): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency {
    const name = FeeScheduleCategoryName.fromJSON(json.name);
    const description = json.description ? FeeScheduleCategoryDescription.fromJSON(json.description) : null;
    const fee = json.fee ? Fee.fromJSON(json.fee) : null;
    const billingCode = json.billingCode ? FeeScheduleBillingCode.fromJSON(json.billingCode) : null;
    const id = Guid.fromJSON(json.id);

    if (!id) throw new Error('Cannot create FeeScheduleCategory without id');
    if (!name) throw new Error('Cannot create FeeScheduleCategory without name');
    if (!description) throw new Error('Cannot create FeeScheduleCategory without description');

    if (json.currencyIsMasked) {
      return new FeeScheduleCategoryMaskedCurrency(
        id,
        name,
        description ?? null,
        billingCode ?? null,
      );
    }

    return new FeeScheduleCategory(
      name,
      description,
      fee,
      billingCode,
      id
    );
  }

  public fromJSON(obj: FeeScheduleCategoryJSON): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null {
    return FeeScheduleCategory.fromJSON(obj);
  }

  toString(): string {
    let feeString: string;
    if (!this.fee) {
      feeString = (this.description ? `${this.name.value}: ${this.description.toString()}\n` : "") + '(Deferred Fee)';
    }
    else {
      feeString = `${this.name.value}: ${this.description?.toString()}\n
      ${!this.fee?.unit ? 'Fee' : 'Rate'}: ${formatCurrency(this.fee?.rate)} ${this?.fee?.rate?.currency.toString() ??
        ''} ${this.fee?.unit ? '/ ' + this.fee.unit : ''}`;
    }
    if (this.billingCode) {
      feeString += `, Billing Code: ${this.billingCode.toString()}`;
    }
    return feeString;
  }

  public toJSON(): FeeScheduleCategoryJSON {
    const fee = this.fee?.toJSON() ?? null;
    const billingCode = this.billingCode?.toJSON() ?? null;

    return {
      id: this.id.toJSON(),
      name: this.name.toJSON(),
      description: this.description?.toJSON() ?? "",
      fee: fee,
      billingCode: billingCode
    };
  }

  get feeString(): string {
    if (!this.fee)
      return `(Deferred Fee)`;
    return `${!this.fee?.unit ? 'Fee'
      : 'Rate'}: ${formatCurrency(this.fee?.rate)} ${this?.fee?.rate?.currency.toString() ?? ''} ${this.fee?.unit
        ? '/ ' + this.fee?.unit : ''}`;
  }

  public static get Prototype() {
    return new FeeScheduleCategory(
      FeeScheduleCategoryName.Prototype,
      FeeScheduleCategoryDescription.Prototype,
      null,
      null,
      Guid.generate()
    );
  }
}

export class FeeScheduleCategoryMaskedCurrency {
  id: Guid;
  name: FeeScheduleCategoryName;
  description: FeeScheduleCategoryDescription | null;
  billingCode: FeeScheduleBillingCode | null;

  constructor(
    id: Guid,
    name: FeeScheduleCategoryName,
    description: FeeScheduleCategoryDescription | null,
    billingCode: FeeScheduleBillingCode | null,
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.billingCode = billingCode;
  }

  public toJSON(): FeeScheduleCategoryJSON {
    return {
      id: this.id.toJSON(),
      name: this.name.toJSON(),
      description: this.description?.toJSON() ?? "",
      fee: null,
      billingCode: this.billingCode?.toJSON() ?? null,
      currencyIsMasked: true
    };
  }

  toString(): string {
    let feeString: string;
    feeString = (this.description ? `${this.name.value}: ${this.description.toString()}\n` : "");
    if (this.billingCode) {
      feeString += `, Billing Code: ${this.billingCode.toString()}`;
    }
    return feeString;
  }

  public isEqualTo(other: object | null | undefined): boolean {
    if (!other) return false;
    if (!(other instanceof FeeScheduleCategoryMaskedCurrency)) return false;
    return (Boolean(
      this.name?.isEqualTo(other.name) &&
      this.description?.isEqualTo(other.description)
    ));
  }

  public clone(): FeeScheduleCategoryMaskedCurrency {
    return new FeeScheduleCategoryMaskedCurrency(
      this.id.clone(),
      this.name.clone(),
      this.description?.clone() ?? null,
      this.billingCode?.clone() ?? null
    );
  }
}
